import { AdsClick } from '@mui/icons-material';
import { Box, CircularProgress, Slider, Tooltip, Typography } from '@mui/material';
import { useRef, useState } from 'react';
import { SegmentationData } from './types';

export const DEFAULT_NUM_SEGMENTS = 10;
export const DEFAULT_SENSITIVITY = 50;

function setPixel(data: Uint8ClampedArray, index: number, r: number, g: number, b: number, a: number) {
  const o = index * 4;
  data[o] = r;
  data[o + 1] = g;
  data[o + 2] = b;
  data[o + 3] = a;
}

function dilate(mask: Uint8Array, width: number, height: number): Uint8Array {
  const out = new Uint8Array(width * height);
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = y * width + x;
      if (!mask[index]) continue;
      out[index] = 1;
      if (x > 0) out[index - 1] = 1;
      if (x < width - 1) out[index + 1] = 1;
      if (y > 0) out[index - width] = 1;
      if (y < height - 1) out[index + width] = 1;
    }
  }
  return out;
}

function findBorders(
  regionMap: Uint16Array,
  width: number,
  height: number,
  selectedRegionIds: Set<number>
): { borders: Uint8Array; selectedBorders: Uint8Array } {
  const N = width * height;
  const borders = new Uint8Array(N);
  const selectedBorders = new Uint8Array(N);
  const hasSelection = selectedRegionIds.size > 0;

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const index = y * width + x;
      const id = regionMap[index];

      const neighbors: number[] = [];
      if (x > 0) neighbors.push(index - 1);
      if (x < width - 1) neighbors.push(index + 1);
      if (y > 0) neighbors.push(index - width);
      if (y < height - 1) neighbors.push(index + width);

      for (const neighborIndex of neighbors) {
        const neighborId = regionMap[neighborIndex];
        if (neighborId !== id) {
          borders[index] = 1;
          if (hasSelection && (selectedRegionIds.has(id) || selectedRegionIds.has(neighborId))) {
            selectedBorders[index] = 1;
          }
        }
      }
    }
  }

  return { borders, selectedBorders };
}

function drawOverlay(canvas: HTMLCanvasElement, segmentation: SegmentationData, selectedRegionIds: Set<number>) {
  const context = canvas.getContext('2d');
  if (!context) return;

  const { regionMap, width, height, regions } = segmentation;
  const hasSelection = selectedRegionIds.size > 0;
  canvas.width = width;
  canvas.height = height;

  const imageData = context.createImageData(width, height);
  const colorOf = new Map(regions.map(r => [r.id, r.color]));

  const { borders, selectedBorders } = findBorders(regionMap, width, height, selectedRegionIds);
  const thickBorder = dilate(borders, width, height);
  const thickSelected = dilate(selectedBorders, width, height);

  for (let index = 0; index < width * height; index++) {
    const regionId = regionMap[index];

    if (thickSelected[index]) {
      setPixel(imageData.data, index, 0, 210, 255, 255);
    } else if (thickBorder[index]) {
      setPixel(imageData.data, index, 255, 255, 255, 200);
    } else if (!hasSelection) {
      const c = colorOf.get(regionId);
      if (c) setPixel(imageData.data, index, c.r, c.g, c.b, 130);
    } else if (!selectedRegionIds.has(regionId)) {
      const c = colorOf.get(regionId);
      if (c) setPixel(imageData.data, index, c.r, c.g, c.b, 150);
    }
  }

  context.putImageData(imageData, 0, 0);
}

const preventDrag = (event: React.DragEvent) => event.preventDefault();

const SliderLabel = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <Tooltip title={tooltip} slotProps={{ tooltip: { sx: { maxWidth: 'none', whiteSpace: 'nowrap' } } }}>
    <Typography
      variant='caption'
      color='text.secondary'
      sx={{ whiteSpace: 'nowrap', cursor: 'default', flexShrink: 0 }}
    >
      {label}
    </Typography>
  </Tooltip>
);

export const SegmentTool = ({
  imageUrl,
  segmentation,
  selectedRegionIds,
  onRegionClick,
  isSegmenting,
  numberSegments,
  sensitivity,
  onNumberSegmentsChange,
  onSensitivityChange,
  containerWidth,
  containerHeight,
}: {
  imageUrl: string;
  segmentation: SegmentationData | undefined;
  selectedRegionIds: Set<number>;
  onRegionClick: (regionId: number) => void;
  isSegmenting: boolean;
  numberSegments: number;
  sensitivity: number;
  onNumberSegmentsChange: (value: number) => void;
  onSensitivityChange: (value: number) => void;
  containerWidth: number;
  containerHeight: number;
}) => {
  const canvasReference = useRef<HTMLCanvasElement>(null);
  const [draftSegments, setDraftSegments] = useState<number | undefined>();
  const [draftSensitivity, setDraftSensitivity] = useState<number | undefined>();

  if (canvasReference.current && segmentation) {
    drawOverlay(canvasReference.current, segmentation, selectedRegionIds);
  }

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!segmentation) return;

    const rect = event.currentTarget.getBoundingClientRect();
    const displayX = event.clientX - rect.left;
    const displayY = event.clientY - rect.top;

    const segX = Math.min(Math.round((displayX * segmentation.width) / containerWidth), segmentation.width - 1);
    const segY = Math.min(Math.round((displayY * segmentation.height) / containerHeight), segmentation.height - 1);

    const regionId = segmentation.regionMap[segY * segmentation.width + segX];
    onRegionClick(regionId);
  };

  return (
    <>
      <Box
        sx={{
          position: 'relative',
          width: containerWidth,
          height: containerHeight,
          cursor: segmentation ? 'crosshair' : 'default',
        }}
        onClick={handleClick}
      >
        <img
          src={imageUrl}
          alt='Editor'
          draggable={false}
          onDragStart={preventDrag}
          style={{
            width: containerWidth,
            height: containerHeight,
            objectFit: 'fill',
            display: 'block',
          }}
        />
        <canvas
          ref={canvasReference}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: containerWidth,
            height: containerHeight,
            pointerEvents: 'none',
            imageRendering: 'pixelated',
          }}
        />
        {isSegmenting && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1.5,
              bgcolor: 'rgba(0,0,0,0.4)',
              backdropFilter: 'blur(2px)',
            }}
          >
            <Box
              sx={{
                '@keyframes scan': {
                  '0%': { top: 0, opacity: 0 },
                  '10%': { opacity: 1 },
                  '90%': { opacity: 1 },
                  '100%': { top: 'calc(100% - 2px)', opacity: 0 },
                },
                position: 'absolute',
                left: 0,
                width: '100%',
                height: '2px',
                background: 'linear-gradient(90deg, transparent, rgba(0,210,255,0.8), transparent)',
                boxShadow: '0 0 12px 2px rgba(0,210,255,0.4)',
                animation: 'scan 2s ease-in-out infinite',
              }}
            />
            <CircularProgress size={28} sx={{ color: 'rgba(0,210,255,0.9)' }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <AdsClick sx={{ fontSize: 16, color: 'rgba(255,255,255,0.85)' }} />
              <Typography variant='caption' sx={{ color: 'white', fontWeight: 500 }}>
                Select regions when ready
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, pt: 1, width: containerWidth, mx: 'auto' }}>
        <SliderLabel label='Fewer regions' tooltip='Controls how many segments the image is divided into' />
        <Slider
          value={draftSegments ?? numberSegments}
          onChange={(_, v) => setDraftSegments(v as number)}
          onChangeCommitted={(_, v) => {
            setDraftSegments(undefined);
            onNumberSegmentsChange(v as number);
          }}
          min={2}
          max={64}
          size='small'
          valueLabelDisplay='auto'
          disabled={isSegmenting}
        />
        <SliderLabel label='More regions' tooltip='Controls how many segments the image is divided into' />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2, width: containerWidth, mx: 'auto' }}>
        <SliderLabel label='Less detail' tooltip='Controls how precisely segment boundaries follow edges' />
        <Slider
          value={draftSensitivity ?? sensitivity}
          onChange={(_, v) => setDraftSensitivity(v as number)}
          onChangeCommitted={(_, v) => {
            setDraftSensitivity(undefined);
            onSensitivityChange(v as number);
          }}
          min={0}
          max={100}
          size='small'
          valueLabelDisplay='auto'
          disabled={isSegmenting}
        />
        <SliderLabel label='More detail' tooltip='Controls how precisely segment boundaries follow edges' />
      </Box>
    </>
  );
};
